// src/components/BookDetails.js

import React, { useState } from "react";
import { useParams, Link } from "react-router-dom";
import booksData from "./booksData";
import "./BookDetails.css";
import { useLanguage } from "../LanguageContext";

function BookDetails() {
  const { id } = useParams(); // Retrieve the book ID from the URL
  const { language } = useLanguage();
  const [selectedImage, setSelectedImage] = useState(null);

  const book = booksData.find((b) => b.id === parseInt(id));

  const openModal = (imgSrc) => setSelectedImage(imgSrc);
  const closeModal = () => setSelectedImage(null);

  if (!book) {
    return (
      <div className="book-details-wrapper">
        <h2>{language === 'ar' ? 'الكتاب غير موجود' : language === 'fr' ? 'Livre introuvable' : 'Book Not Found'}</h2>
        <Link to="/" className="back-button">
          {language === 'ar' ? 'رجوع' : language === 'fr' ? 'Retour' : 'Go Back'}
        </Link>
      </div>
    );
  }

  // Helper function to render order info with icons and links
  const renderOrderInfo = (info) => {
    if (!info) return null;
    
    return info.split('\n').map((line, index) => {
      const trimmedLine = line.trim();
      if (!trimmedLine) return <br key={index} />;

      // Regex to find markdown links: [text](url)
      const linkRegex = /\[([^\]]+)\]\((https?:\/\/[^)]+)\)/g;
      const parts = [];
      let lastIndex = 0;
      let match;

      const isArabic = /[\u0600-\u06FF]/.test(trimmedLine);
      const lineDir = isArabic ? 'rtl' : 'ltr';

      while ((match = linkRegex.exec(trimmedLine)) !== null) {
        // Push text before match
        if (match.index > lastIndex) {
          parts.push(trimmedLine.substring(lastIndex, match.index));
        }
        // Push the link
        parts.push(
          <a 
            key={match.index} 
            href={match[2]} 
            target="_blank" 
            rel="noopener noreferrer"
            className="order-link"
          >
            {match[1]}
          </a>
        );
        lastIndex = linkRegex.lastIndex;
      }
      
      // Push remaining text
      if (lastIndex < trimmedLine.length) {
        parts.push(trimmedLine.substring(lastIndex));
      }

      return (
        <p key={index} className="order-item" dir={lineDir}>
          <i className="fas fa-map-marker-alt"></i> 
          <span className="order-item-text">{parts.length > 0 ? parts : trimmedLine}</span>
        </p>
      );
    });
  };

  return (
    <div className="book-details-wrapper" dir={language === 'ar' ? 'rtl' : 'ltr'}>
      <Link to="/" className="back-button">
        {language === 'ar' ? '← العودة' : language === 'fr' ? '← Retour' : '← Back'}
      </Link>
      
      <div className="book-details">
        <div className="book-image-click-container" onClick={() => openModal(book.image)} title={language === 'ar' ? 'انقر للتكبير' : language === 'fr' ? 'Cliquez pour agrandir' : 'Click to enlarge'}>
          <img
            src={book.image}
            alt={book.alt}
            className="book-details-image"
            loading="lazy"
          />
          <div className="zoom-overlay">
            <i className="fas fa-search-plus"></i>
          </div>
        </div>

        {selectedImage && (
          <div className="image-modal-overlay" onClick={closeModal}>
            <div className="modal-content" onClick={(e) => e.stopPropagation()}>
              <button className="close-modal" onClick={closeModal}>&times;</button>
              <img src={selectedImage} alt={book.alt} className="modal-image" />
            </div>
          </div>
        )}

        <div className="book-details-info">
          <div className="title-with-social">
            <h2>{book.title[language] || book.title.fr}</h2>
            {book.goodreads_link && (
              <a 
                href={book.goodreads_link} 
                target="_blank" 
                rel="noopener noreferrer" 
                className="goodreads-icon-link"
                title="Goodreads"
              >
                <i className="fab fa-goodreads-g"></i>
              </a>
            )}
          </div>
          
          {book.quote_fr && (
            <div className="book-quote">
              <p className="quote-text">
                {language === 'ar' ? book.quote_ar : language === 'fr' ? book.quote_fr : book.quote_en}
              </p>
              <p className="quote-author">- {book.quote_author}</p>
            </div>
          )}

          <p><strong>{language === 'ar' ? 'المؤلف:' : language === 'fr' ? 'Auteur:' : 'Author:'}</strong> {book.author}</p>

          <div className="description-section">
            <h3>{language === 'ar' ? 'الوصف' : language === 'fr' ? 'Description' : 'Description'}</h3>
            <p style={{ whiteSpace: 'pre-line' }}>{language === 'ar' ? book.description_ar : language === 'fr' ? book.description_fr : book.description_en}</p>
          </div>

          {/* Ordering Information */}
          <div className="order-section">
            <h3>{language === 'ar' ? 'كيفية الطلب' : language === 'fr' ? 'Comment commander' : 'How to order'}</h3>
            <div className="order-content">
              {renderOrderInfo(book.order_info)}
            </div>
            
            {book.goodreads_link && (
              <div className="goodreads-button-container">
                <a 
                  href={book.goodreads_link} 
                  target="_blank" 
                  rel="noopener noreferrer" 
                  className="goodreads-btn"
                >
                  <i className="fab fa-goodreads"></i>
                  {language === 'ar' ? 'تصفح الآراء على Goodreads' : language === 'fr' ? 'Consulter les avis sur Goodreads' : 'View reviews on Goodreads'}
                </a>
              </div>
            )}
          </div>

          {/* Additional Images */}
          {book.additional_images && book.additional_images.length > 0 && (
            <div className="additional-images">
              <h3>{language === 'ar' ? 'صور إضافية' : language === 'fr' ? 'Images supplémentaires' : 'Additional Images'}</h3>
              <div className="images-container">
                {book.additional_images.map((img, index) => (
                  <div 
                    key={index} 
                    className="additional-image-wrapper zoomable" 
                    onClick={() => openModal(img)}
                    title={language === 'ar' ? 'انقر للتكبير' : language === 'fr' ? 'Cliquez pour agrandir' : 'Click to enlarge'}
                  >
                    <img
                      src={img}
                      alt={`${book.title[language] || book.title.fr} ${index + 1}`}
                      className="additional-image"
                      loading="lazy"
                    />
                    <div className="zoom-overlay small">
                      <i className="fas fa-search-plus"></i>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Discovery Link */}
          {book.id !== 3 && (
            <div className="discovery-section">
              <Link to="/books/3" className="discovery-button">
                {language === 'ar' ? 'اكتشف أيضاً ديواني الأخير "Habiter le presque"' : language === 'fr' ? 'Découvrir aussi mon dernier recueil "Habiter le presque"' : 'Discover also my latest collection "Habiter le presque"'}
              </Link>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default BookDetails;
